import { API_BASE_URL } from "./constants";

/**
 * Creates an authenticated fetch request with the access token
 * @param endpoint The API endpoint to call (without the base URL)
 * @param token The authentication token
 * @param options Request options (method, body, etc.)
 * @returns Promise with the response data
 */
export async function fetchWithAuth(
  endpoint: string,
  token: string,
  options: RequestInit = {},
) {
  // Check if we have a token
  if (!token) {
    throw new Error("Token de autenticación faltante");
  }

  // Merge headers with authorization
  const headers = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  } as Record<string, string>;

  const url = `${API_BASE_URL}${endpoint}`;

  // Make the request
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Try to get more detailed error information
    let errorDetail = "";
    try {
      const errorData = await response.json();
      errorDetail =
        errorData.message || errorData.error || JSON.stringify(errorData);
    } catch (e) {
      // If we can't parse JSON, just use status text
      errorDetail = response.statusText;
    }

    if (response.status === 401) {
      throw new Error(`Error de autenticación: ${errorDetail}`);
    } else {
      throw new Error(`Error de API (${response.status}): ${errorDetail}`);
    }
  }

  // For DELETE requests that don't return content
  if (options.method === "DELETE" && response.status === 204) {
    return null;
  }

  return await response.json();
}

/**
 * Upload a driver picture using multipart/form-data with optional auth token.
 * Returns JSON (e.g., { url: string }).
 */
export async function uploadDriverPicture({
  apiBaseUrl,
  driverId,
  file,
  token,
}: {
  apiBaseUrl: string;
  driverId: number;
  file: File;
  token?: string;
}): Promise<any> {
  const form = new FormData();
  form.append("file", file);

  const res = await fetch(`${apiBaseUrl}/driver/${driverId}/picture`, {
    method: "POST",
    body: form,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) {
    throw new Error(await res.text());
  }

  return res.json();
}

/**
 * Creates an authenticated fetch request using a full absolute URL
 * (useful for calling other microservices that don't share API_BASE_URL)
 */
export async function fetchAbsoluteWithAuth(
  url: string,
  token: string,
  options: RequestInit = {},
): Promise<any> {
  if (!token) {
    throw new Error("Token de autenticación faltante");
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...options.headers,
  } as Record<string, string>;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorDetail = "";
    try {
      const errorData = await response.json();
      errorDetail =
        errorData.message || errorData.error || JSON.stringify(errorData);
    } catch (e) {
      errorDetail = response.statusText;
    }

    if (response.status === 401) {
      throw new Error(`Error de autenticación: ${errorDetail}`);
    } else {
      throw new Error(`Error de API (${response.status}): ${errorDetail}`);
    }
  }

  if (options.method === "DELETE" && response.status === 204) {
    return null;
  }

  // Some PATCH endpoints might return empty body
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
}

/**
 * Creates a service with common API methods for a specific resource
 * @param resourcePath The base path for the resource (e.g., '/drivers')
 * @param getToken Function that returns the authentication token
 * @param customEndpoints Optional object with custom endpoints for specific operations
 * @returns Object with CRUD methods for the resource
 */
export function createApiService<T>(
  resourcePath: string,
  getToken: () => Promise<string>,
  customEndpoints?: {
    getAll?: string;
    getById?: string;
    create?: string;
    update?: string;
    delete?: string;
    getMetricsByDriverId?: string;
  },
) {
  return {
    getAll: async (): Promise<T[]> => {
      const token = await getToken();
      const endpoint = customEndpoints?.getAll || resourcePath;
      return fetchWithAuth(endpoint, token);
    },

    getById: async (id: number | string): Promise<T> => {
      const token = await getToken();
      const endpoint = customEndpoints?.getById
        ? `${customEndpoints.getById}/web/${id}`
        : `${resourcePath}/${id}`;
      return fetchWithAuth(endpoint, token);
    },

    create: async (data: Partial<T>): Promise<T> => {
      const token = await getToken();
      const endpoint = customEndpoints?.create || resourcePath;
      return fetchWithAuth(endpoint, token, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },

    update: async (id: number | string, data: Partial<T>): Promise<T> => {
      const token = await getToken();
      const endpoint = customEndpoints?.update
        ? `${customEndpoints.update}/${id}`
        : `${resourcePath}/${id}`;
      return fetchWithAuth(endpoint, token, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },

    delete: async (id: number | string): Promise<void> => {
      const token = await getToken();
      const endpoint = customEndpoints?.delete
        ? `${customEndpoints.delete}/${id}`
        : `${resourcePath}/${id}`;
      return fetchWithAuth(endpoint, token, {
        method: "DELETE",
      });
    },
    getMetricsByDriverId: async (id: number | string): Promise<T> => {
      const token = await getToken();
      const endpoint = customEndpoints?.getMetricsByDriverId
        ? `${customEndpoints.getMetricsByDriverId}/${id}`
        : `${resourcePath}/${id}/metrics`;
      return fetchWithAuth(endpoint, token);
    },
  };
}

export async function apiRequest(
  endpoint: string,
  options: RequestInit = {},
): Promise<any> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Make the request
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    // Try to get more detailed error information
    let errorDetail = "";
    try {
      const errorData = await response.json();
      errorDetail =
        errorData.message || errorData.error || JSON.stringify(errorData);
    } catch (e) {
      // If we can't parse JSON, just use status text
      errorDetail = response.statusText;
    }

    if (response.status === 401) {
      throw new Error(`Error de autenticación: ${errorDetail}`);
    } else {
      throw new Error(`Error de API (${response.status}): ${errorDetail}`);
    }
  }

  return await response.json();
}
